import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import QRCode from "qrcode";
import type {
  ClientMessage,
  EvidenceItem,
  EvidenceSource,
  PollQuestion,
  SessionSnapshot,
  ServerMessage,
} from "@shared/types";
import { fetchSnapshot, joinSession, reportUrl } from "./api";
import { getFacilitatorToken } from "./Landing";
import { useSessionSocket } from "./useSessionSocket";
import { Radar } from "./Radar";
import { Briefing } from "./Briefing";
import { MaturityStages } from "./MaturityStages";
import { heatColor, maturityColor, maturityLabel, mean, round2, MATURITY_COLORS } from "@shared/scoring";
import { MATURITY_SCALE } from "./scale";

type View = "summary" | "heatmap";
type Filter = "all" | EvidenceSource;

const SOURCE_GLYPH: Record<string, string> = {
  poll: "▲",
  lucid: "◆",
  copilot: "✦",
  forms: "✦",
  csv: "▣",
  teams: "●",
};

const CHAT_AVATAR_COLORS = ["#7C3AED", "#0078d4", "#00b4d8", "#f59e0b", "#10b981", "#e11d48", "#8b5cf6"];
function chatColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CHAT_AVATAR_COLORS[h % CHAT_AVATAR_COLORS.length];
}
function chatInitials(name: string): string {
  return name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}

function timeOfDay(iso: string): string {
  const d = new Date(iso);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, "0")} ${ampm}`;
}

// For imported evidence the spreadsheet's Day (e.g. "Day 1") and Session
// (time of day, e.g. "After Lunch") are shown in their own columns; live poll
// responses have neither, so When falls back to the ingestion clock-time and
// Day is derived from the calendar date the response landed on.
// Workshop runs Jun 2 (Day 1) → Jun 3 (Day 2), 2026.
const WORKSHOP_DAYS: Record<string, string> = {
  "2026-06-02": "Day 1",
  "2026-06-03": "Day 2",
};
function dayFromDate(iso: string): string {
  const d = new Date(iso);
  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return WORKSHOP_DAYS[key] ?? "";
}
function evidenceDay(e: EvidenceItem): string {
  return e.day || dayFromDate(e.createdAt) || "—";
}
function evidenceWhen(e: EvidenceItem): string {
  return e.sessionPart || timeOfDay(e.createdAt);
}

export function Dashboard() {
  const { sessionId } = useParams();
  const [snap, setSnap] = useState<SessionSnapshot | null>(null);
  const [activeVector, setActiveVector] = useState(1);
  const [filter, setFilter] = useState<Filter>("all");
  const [audience, setAudience] = useState<"all" | "participant">("all");
  const [viewAll, setViewAll] = useState(false);
  const [view, setView] = useState<View>("summary");
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [qrExpanded, setQrExpanded] = useState(false);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [now, setNow] = useState(Date.now());
  const [loadError, setLoadError] = useState("");
  // Instant, styled hover tooltip (position:fixed so it isn't clipped by the
  // scrollable feed / evidence table).
  const [tip, setTip] = useState<{ text: string; x: number; y: number; above: boolean } | null>(null);
  const showTip = useCallback((e: MouseEvent, text?: string | null) => {
    if (!text) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const above = r.bottom > window.innerHeight - 170; // flip up near the bottom edge
    setTip({
      text,
      x: Math.max(8, Math.min(r.left, window.innerWidth - 312)),
      y: above ? r.top - 6 : r.bottom + 6,
      above,
    });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);
  const tipProps = useCallback(
    (text?: string | null) => ({
      onMouseEnter: (e: MouseEvent) => showTip(e, text),
      onMouseLeave: hideTip,
    }),
    [showTip, hideTip]
  );
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const facToken = sessionId ? getFacilitatorToken(sessionId) : "";

  const patchSnap = useCallback((fn: (s: SessionSnapshot) => SessionSnapshot) => {
    setSnap((prev) => (prev ? fn(prev) : prev));
  }, []);

  const onMessage = useCallback(
    (msg: ServerMessage) => {
      switch (msg.type) {
        case "state_snapshot":
          setSnap(msg.snapshot);
          break;
        case "scores_updated":
          patchSnap((s) => ({ ...s, vectors: msg.vectors, participantCount: msg.participantCount }));
          break;
        case "response_added":
          patchSnap((s) => ({
            ...s,
            vectors: msg.vectors,
            evidence: [msg.evidence, ...s.evidence].slice(0, 200),
          }));
          break;
        case "evidence_added":
          patchSnap((s) => ({ ...s, evidence: [...msg.evidence, ...s.evidence].slice(0, 200) }));
          break;
        case "participant_joined":
          patchSnap((s) => ({
            ...s,
            participantCount: msg.participantCount,
            participants: s.participants.some((p) => p.id === msg.participant.id)
              ? s.participants.map((p) => (p.id === msg.participant.id ? msg.participant : p))
              : [...s.participants, msg.participant],
          }));
          break;
        case "participant_left":
          patchSnap((s) => ({ ...s, participantCount: msg.participantCount }));
          break;
        case "presence_updated":
          patchSnap((s) => ({ ...s, participants: msg.participants }));
          break;
        case "chat_added":
          patchSnap((s) => ({ ...s, chat: [msg.message, ...s.chat].slice(0, 100) }));
          break;
        case "ingestion_completed":
          patchSnap((s) => ({ ...s, vectors: msg.vectors, heatmap: msg.heatmap }));
          // refresh participants/evidence fully after a batch import
          if (sessionId) fetchSnapshot(sessionId).then(setSnap).catch(() => {});
          break;
        case "session_ended":
          patchSnap((s) => ({ ...s, session: { ...s.session, status: "ended" } }));
          break;
      }
    },
    [patchSnap, sessionId]
  );

  const { status, send } = useSessionSocket(sessionId ?? null, { onMessage });

  // Initial load (fallback in case the first ws snapshot is delayed).
  useEffect(() => {
    if (!sessionId) return;
    fetchSnapshot(sessionId)
      .then(setSnap)
      .catch(() => setLoadError("Session not found."));
  }, [sessionId]);

  // Announce dashboard (with facilitator token) once connected.
  useEffect(() => {
    if (status === "open") send({ type: "dashboard_hello", facilitatorToken: facToken || undefined });
  }, [status, send, facToken]);

  // QR code for the join URL.
  useEffect(() => {
    if (!snap) return;
    const joinUrl = `${location.origin}/join/${snap.session.joinCode}`;
    QRCode.toDataURL(joinUrl, { margin: 1, width: 512 }).then(setQrDataUrl).catch(() => {});
  }, [snap?.session.joinCode]);

  // Tick clock.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-scroll the discussion to the newest message (rendered at the bottom).
  useEffect(() => {
    if (teamsOpen) chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [snap?.chat.length, teamsOpen]);

  // Esc closes the QR lightbox.
  useEffect(() => {
    if (!qrExpanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setQrExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [qrExpanded]);

  const elapsed = useMemo(() => {
    if (!snap?.session.startedAt) return "00:00:00";
    const secs = Math.max(0, Math.floor((now - new Date(snap.session.startedAt).getTime()) / 1000));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
  }, [snap?.session.startedAt, now]);

  const wallClock = useMemo(() => timeOfDay(new Date(now).toISOString()), [now]);

  if (loadError) {
    return (
      <div className="center-screen">
        <h1>{loadError}</h1>
        <Link to="/" className="btn-primary" style={{ textDecoration: "none" }}>Back to start</Link>
      </div>
    );
  }
  if (!snap) {
    return (
      <div className="center-screen">
        <div className="spinner" />
        <p>Loading session…</p>
      </div>
    );
  }

  // "Participants Only" hides speaker-attributed evidence everywhere on the page
  // (everything non-speaker reads as a participant, matching the row badge logic).
  // The radar scores are recomputed from the participant-only evidence so the
  // whole page — radar, live feed, insights, summary table — stays in sync.
  const audienceEvidence = audience === "all" ? snap.evidence : snap.evidence.filter((e) => e.kind !== "speaker");
  const audienceVectors =
    audience === "all"
      ? snap.vectors
      : snap.vectors.map((v) => {
          const scores = audienceEvidence
            .filter((e) => e.vectorIndex === v.index && e.scoreText)
            .map((e) => parseFloat(e.scoreText!))
            .filter((n) => Number.isFinite(n));
          if (!scores.length) return { ...v, responseCount: 0 };
          const score = round2(mean(scores)!);
          return { ...v, score, maturityLabel: maturityLabel(score), responseCount: scores.length };
        });

  const vec = audienceVectors.find((v) => v.index === activeVector) ?? audienceVectors[0];
  const baseEvidence = viewAll ? audienceEvidence : audienceEvidence.filter((e) => e.vectorIndex === activeVector);
  const matchesFilter = (e: EvidenceItem) =>
    filter === "all" || e.source === filter || (filter === "copilot" && e.source === "forms");
  const filtered = baseEvidence.filter(matchesFilter);
  const counts = {
    all: baseEvidence.length,
    poll: baseEvidence.filter((e) => e.source === "poll").length,
    lucid: baseEvidence.filter((e) => e.source === "lucid").length,
    copilot: baseEvidence.filter((e) => e.source === "copilot" || e.source === "forms").length,
  };
  const colorFor = (idx: number) => audienceVectors.find((v) => v.index === idx)?.color ?? "var(--violet)";
  const totalResponses = audienceVectors.reduce((s, v) => s + v.responseCount, 0);
  const avgScore = mean(audienceVectors.map((v) => v.score)) ?? 0;

  const offline = status !== "open";

  const selectVector = (i: number) => {
    setActiveVector(i);
    setFilter("all");
    setViewAll(false);
  };

  return (
    <div className="app">
      {/* SIDEBAR */}
      <aside className="sidebar">
        <div className="sidebar-logo"><img src="/brand/ey-mark.svg" alt="EY" style={{ height: 30 }} /></div>
        <div className="nav-item active" title="Dashboard">
          <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
        </div>
        <div className={`nav-item ${teamsOpen ? "active" : ""}`} title="Presence & activity" onClick={() => setTeamsOpen((o) => !o)}>
          <svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
        </div>
        <Link to={`/s/${sessionId}/ingest`} className="nav-item nav-csv" title="Data ingestion" style={{ textDecoration: "none" }}>
          <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
        </Link>
        <div className="sidebar-spacer" />
        <div className="session-timer">
          <div className="session-timer-dot" />
          <div className="session-timer-label">Session<br />Time</div>
          <div className="session-timer-value">{elapsed}</div>
        </div>
      </aside>

      {/* TOP BAR */}
      <header className="topbar">
        <div className="topbar-title">
          <h1>{snap.session.title}</h1>
        </div>
        <div className="topbar-spacer" />
        <div className={`live-badge ${snap.session.kind === "sandbox" ? "sandbox" : ""}`}>
          <span className={`live-dot ${offline ? "offline" : ""}`} />
          {snap.session.status === "ended"
            ? "ENDED"
            : offline
            ? "RECONNECTING"
            : snap.session.kind === "sandbox"
            ? "SANDBOX"
            : "LIVE SESSION"}
        </div>
        <div className="session-info">
          <span>{snap.session.sessionLabel}</span>
          <span className="session-sep">•</span>
          <span>{wallClock}</span>
        </div>
        {snap.session.meetingLink && (
          <a
            className="live-badge"
            href={snap.session.meetingLink}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none", color: "var(--ms-blue)", borderColor: "color-mix(in oklch, var(--ms-blue) 40%, transparent)" }}
            title="Open the meeting"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 10l4.55-2.28A1 1 0 0121 8.62v6.76a1 1 0 01-1.45.9L15 14M4 6h9a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2z" /></svg>
            Join meeting
          </a>
        )}
        <div className="participant-count">
          <svg viewBox="0 0 24 24"><circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0-3-3.85" /></svg>
          <div>
            <div className="participant-count-num">
              {snap.participantCount}
              {snap.session.expectedParticipants ? (
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)" }}> / {snap.session.expectedParticipants}</span>
              ) : null}
            </div>
            <div className="participant-count-label">
              {snap.session.expectedParticipants ? "Joined / Expected" : "Participants"}
            </div>
          </div>
        </div>
        <div className="qr-block">
          <div className="qr-box" onClick={() => setQrExpanded(true)} style={{ cursor: "pointer" }} title="Expand QR">
            {qrDataUrl && <img src={qrDataUrl} alt="Join QR" />}
          </div>
          <div className="qr-info">
            <div className="qr-join">Join at</div>
            <div className="qr-url">{location.host}/join</div>
            <div className="qr-code">Code: {snap.session.joinCode}</div>
          </div>
          <button className="qr-expand" onClick={() => setQrExpanded(true)} title="Expand QR & join code" aria-label="Expand QR code">
            <svg viewBox="0 0 24 24"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
          </button>
        </div>
      </header>

      {/* MAIN */}
      {view === "summary" ? (
        <main className="main">
          {/* LEFT: Polling (facilitator presentation view) */}
          <div className="left-col">
            <div className="panel polling-panel">
              <div className="polling-header">
                <div className="polling-title">
                  <svg viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
                  POLLING
                </div>
              </div>
              <KioskPoll
                sessionId={sessionId!}
                questions={snap.questions}
                joinCode={snap.session.joinCode}
                send={send}
                onActiveVector={selectVector}
              />
              <button className="briefing-cta" onClick={() => setBriefingOpen(true)}>
                <span className="briefing-cta-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 4.6L18.5 9l-3.6 3 1.1 5L12 14.8 8 17l1.1-5L5.5 9l4.6-1.4z" /></svg>
                </span>
                <span className="briefing-cta-text">
                  <span className="briefing-cta-title">What makes a workforce AI-ready?</span>
                  <span className="briefing-cta-sub">Open the leadership briefing — the 5 readiness factors and the 2025 survey data behind them.</span>
                </span>
                <span className="briefing-cta-arrow">→</span>
              </button>
            </div>
          </div>

          {/* RIGHT: Radar + Insights */}
          <div className="right-col">
            <div className="panel radar-panel">
              <div className="radar-header">
                <div>
                  <div className="radar-title">Readiness Radar</div>
                  <div className="radar-subtitle">Click any dimension to explore the data behind the score.</div>
                </div>
                <div className="radar-legend">
                  <div className="legend-item"><div className="legend-line" style={{ background: totalResponses > 0 ? maturityColor(avgScore) : "var(--border)" }} /><span>Current State</span></div>
                  <div className="legend-item"><div className="legend-dashed" /><span>EY Benchmark</span></div>
                </div>
              </div>
              <div className="radar-body radar-body-solo">
                <div className="radar-svg-wrap">
                  <Radar vectors={audienceVectors} activeVector={activeVector} onSelect={selectVector} hasData={totalResponses > 0} />
                </div>
                <div className="live-feed-card">
                  <div className="live-feed-head">
                    <span className="live-dot" />
                    <span className="live-feed-title">Live Data</span>
                    <span className="live-feed-count">{audienceEvidence.length}</span>
                  </div>
                  <div className="live-feed-list">
                    {audienceEvidence.length === 0 ? (
                      <div className="live-feed-empty">Waiting for the first response…</div>
                    ) : (
                      audienceEvidence.slice(0, 40).map((e) => (
                        <div
                          className="feed-row"
                          key={e.id}
                          {...tipProps(
                            [
                              e.detail,
                              e.excerpt ? `"${e.excerpt}"` : null,
                              e.scoreText,
                              [e.sourceLabel, e.day, e.sessionPart, e.segment].filter(Boolean).join(" · "),
                            ]
                              .filter(Boolean)
                              .join("\n")
                          )}
                        >
                          <div className={`source-icon ${e.source}`}>{SOURCE_GLYPH[e.source] ?? "▲"}</div>
                          <div className="feed-main">
                            <div className="feed-detail">{e.excerpt || e.detail}</div>
                            <div className="feed-meta">{e.sourceLabel} · {evidenceWhen(e)}</div>
                          </div>
                          {e.scoreText && <div className="feed-score">{e.scoreText.split(" · ")[0]}</div>}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <div className="radar-scale-legend">
                <span className="radar-scale-label">Readiness Scale</span>
                {["Ideating", "Experimenting", "Optimizing", "Transforming", "Leading"].map((s, i) => (
                  <span key={s} className="radar-scale-item" style={{ color: MATURITY_COLORS[i], fontWeight: 600 }}>
                    <span className="radar-scale-dot" style={{ background: MATURITY_COLORS[i] }} />
                    {i + 1} – {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="panel insights-panel">
              <div className="insights-header">
                <div className="insights-title">{viewAll ? "All Responses — Live" : "Vector Insights"}</div>
                <div className="insights-header-right">
                  <div className="audience-toggle" role="group" aria-label="Audience filter">
                    <button
                      className={`aud-opt ${audience === "all" ? "active" : ""}`}
                      onClick={() => setAudience("all")}
                    >
                      All Data
                    </button>
                    <button
                      className={`aud-opt ${audience === "participant" ? "active" : ""}`}
                      onClick={() => setAudience("participant")}
                    >
                      Participants Only
                    </button>
                  </div>
                  <button
                    className="view-all-link"
                    onClick={() => { setViewAll((v) => !v); setFilter("all"); }}
                  >
                    {viewAll ? "← Back to vector" : "View all evidence →"}
                  </button>
                </div>
              </div>
              <div className="insights-body">
                {viewAll ? (
                  <div className="vector-detail">
                    <div className="vd-num" style={{ background: "var(--violet)" }}>★</div>
                    <div className="vd-label">All Vectors</div>
                    <div className="vd-score-row">
                      <div className="vd-score" style={{ color: "var(--fg-inv)" }}>{totalResponses}</div>
                      <div className="vd-denom">responses</div>
                    </div>
                    <div className="vd-maturity">{audienceEvidence.length} evidence items</div>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 10, lineHeight: 1.5 }}>
                      Click a vector on the right ↗ to drill into one dimension.
                    </div>
                  </div>
                ) : (
                  <div className="vector-detail">
                    <div className="vd-num" style={{ background: maturityColor(vec.score) }}>{vec.index}</div>
                    <div className="vd-label">{vec.shortLabel}</div>
                    <div className="vd-desc">{vec.label}</div>
                    <div className="vd-score-row">
                      <div className="vd-score" style={{ color: maturityColor(vec.score) }}>{vec.score.toFixed(1)}</div>
                      <div className="vd-denom">/ 5</div>
                    </div>
                    <div className="vd-maturity" style={{ color: maturityColor(vec.score) }}>{vec.maturityLabel}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>{vec.responseCount} live responses</div>
                  </div>
                )}
                <div className="insights-right">
                  <div className="source-filters">
                    <div className={`source-filter ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>All Sources <span className="source-count">{counts.all}</span></div>
                    <div className={`source-filter ${filter === "poll" ? "active" : ""}`} onClick={() => setFilter("poll")}>Polls <span className="source-count">{counts.poll}</span></div>
                    <div className={`source-filter ${filter === "lucid" ? "active" : ""}`} onClick={() => setFilter("lucid")}>Lucidchart <span className="source-count">{counts.lucid}</span></div>
                    <div className={`source-filter ${filter === "copilot" ? "active" : ""}`} onClick={() => setFilter("copilot")}>Copilot / Forms <span className="source-count">{counts.copilot}</span></div>
                  </div>
                  {viewAll && (
                    <div className="all-hint">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></svg>
                      Streaming every response live. Click a vector on the right ↗ to focus on one dimension.
                    </div>
                  )}
                  <div className="evidence-table">
                    {filtered.length > 0 && (
                      <div className="evidence-row evidence-head">
                        <div>Source</div>
                        <div>Who</div>
                        <div>Evidence</div>
                        <div>Segment</div>
                        <div className="col-score">Score</div>
                        <div className="col-day">Day</div>
                        <div className="col-when">When</div>
                      </div>
                    )}
                    {filtered.length ? (
                      filtered.map((e: EvidenceItem) => (
                        <div className="evidence-row" key={e.id}>
                          <div className="source-pill" {...tipProps(e.sourceLabel)}>
                            {viewAll && (
                              <span className="vec-chip" style={{ background: colorFor(e.vectorIndex) }} title={`Vector ${e.vectorIndex}`}>{e.vectorIndex}</span>
                            )}
                            <div className={`source-icon ${e.source}`}>{SOURCE_GLYPH[e.source] ?? "▲"}</div>
                            <span className="source-pill-label">{e.sourceLabel}</span>
                          </div>
                          <div className="evidence-actor" {...tipProps(e.detail)}>
                            <span className="actor-name">{e.detail || "—"}</span>
                            <span className={`kind-badge ${e.kind === "speaker" ? "speaker" : "participant"}`}>{e.kind === "speaker" ? "Speaker" : "Participant"}</span>
                          </div>
                          <div className={e.excerpt ? "evidence-excerpt" : "evidence-detail"} {...tipProps(e.excerpt || e.detail)}>{e.excerpt || "—"}</div>
                          <div className="evidence-segment" {...tipProps(e.segment)}>{e.segment || "—"}</div>
                          <div className="evidence-score" {...tipProps(e.scoreText)}>{e.scoreText ? e.scoreText.split(" · ")[0] : ""}</div>
                          <div className="evidence-day" {...tipProps([e.day, e.sessionPart].filter(Boolean).join(" · "))}>{evidenceDay(e)}</div>
                          <div className="evidence-when" {...tipProps([e.sessionPart, e.segment].filter(Boolean).join(" · "))}>{evidenceWhen(e)}</div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: 16, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>
                        {viewAll ? "No responses yet. They'll stream in here live." : "No evidence for this filter yet."}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      ) : (
        <HeatmapView snap={snap} />
      )}

      {/* BOTTOM BAR */}
      <nav className="bottombar">
        <div className={`bottom-tab ${view === "summary" ? "active" : ""}`} onClick={() => setView("summary")}>
          <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
          Summary View
        </div>
        <div className={`bottom-tab ${view === "heatmap" ? "active" : ""}`} onClick={() => setView("heatmap")}>
          <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 3v18M15 3v18" /></svg>
          Heatmap View
        </div>
        <a className="bottom-tab" href={reportUrl(sessionId!)} target="_blank" rel="noreferrer">
          <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
          Download Report
        </a>
        <Link className="bottom-tab" to={`/s/${sessionId}/ingest`} style={{ marginLeft: "auto", textDecoration: "none" }}>
          <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
          Load CSV
        </Link>
      </nav>

      {/* PRESENCE PANEL */}
      {teamsOpen && (
        <div className="teams-panel">
          <div className="teams-panel-header">
            <div className="teams-panel-title">
              <img className="teams-logo-img" src="/brand/teams.png" alt="Microsoft Teams" />
              Live Presence
            </div>
            <button className="close-teams" onClick={() => setTeamsOpen(false)}>
              <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
          <div className="presence-list">
            <div className="presence-title">In the room ({snap.participants.filter((p) => p.presence === "online").length} online)</div>
            {snap.participants.length ? (
              snap.participants.map((p) => {
                const label = p.displayName || `Participant ${p.id.slice(0, 4)}`;
                const initials = label.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
                return (
                  <div className="presence-item" key={p.id}>
                    <div className={`presence-avatar ${p.presence}`}>{initials}</div>
                    <div>
                      <div className="presence-name">{label}</div>
                      <div className="presence-role">{p.role || p.department || "Participant"}</div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ fontSize: 12, color: "var(--muted)", padding: "8px 0" }}>No participants yet. Share code {snap.session.joinCode}.</div>
            )}
          </div>
          <div className="presence-title" style={{ padding: "10px 14px 0" }}>Discussion ({snap.chat.length})</div>
          <div className="chat-feed">
            {snap.chat.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--muted)" }}>
                Messages from participants appear here live after they finish the poll.
              </div>
            ) : (
              [...snap.chat].reverse().map((m) => (
                <div className="dchat-msg" key={m.id}>
                  <div className="dchat-avatar" style={{ background: chatColor(m.author) }}>{chatInitials(m.author)}</div>
                  <div className="dchat-body">
                    <div className="chat-header">
                      <span className="chat-name">{m.author}</span>
                      <span className="chat-time">{timeOfDay(m.createdAt)}</span>
                    </div>
                    <div className="dchat-bubble">{m.text}</div>
                  </div>
                </div>
              ))
            )}
            <div ref={chatBottomRef} />
          </div>
        </div>
      )}

      {/* QR LIGHTBOX */}
      {qrExpanded && (
        <div className="qr-lightbox" onClick={() => setQrExpanded(false)}>
          <div className="qr-lightbox-card" onClick={(e) => e.stopPropagation()}>
            <button className="qr-lightbox-close" onClick={() => setQrExpanded(false)} aria-label="Close">
              <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
            <div className="qr-lightbox-eyebrow">Scan to join the workshop</div>
            {qrDataUrl && <img className="qr-lightbox-img" src={qrDataUrl} alt="Join QR code" />}
            <div className="qr-lightbox-url">{location.host}/join</div>
            <div className="qr-lightbox-code-label">Join code</div>
            <div className="qr-lightbox-code">{snap.session.joinCode}</div>
            {snap.session.meetingLink && (
              <a className="qr-lightbox-meeting" href={snap.session.meetingLink} target="_blank" rel="noreferrer">
                Open meeting link →
              </a>
            )}
          </div>
        </div>
      )}

      {briefingOpen && <Briefing vectors={snap.vectors} onClose={() => setBriefingOpen(false)} />}

      {tip &&
        createPortal(
          <div className={`hover-tip${tip.above ? " above" : ""}`} style={{ left: tip.x, top: tip.y }}>{tip.text}</div>,
          document.body
        )}
    </div>
  );
}

// In-person kiosk on the facilitator's left card: a walk-up participant enters
// name + title, answers the maturity questions one at a time (feeding the live
// radar), gets a thank-you, then it auto-resets after 5s for the next person.
function KioskPoll({
  sessionId,
  questions,
  joinCode,
  send,
  onActiveVector,
}: {
  sessionId: string;
  questions: PollQuestion[];
  joinCode: string;
  send: (m: ClientMessage) => boolean;
  onActiveVector: (i: number) => void;
}) {
  const [phase, setPhase] = useState<"enter" | "polling" | "done">("enter");
  const [pid, setPid] = useState<string | null>(null);
  const [qIdx, setQIdx] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  async function start() {
    setBusy(true);
    try {
      const p = await joinSession(sessionId, {}); // anonymous — no name/title collected
      setPid(p.id);
      setQIdx(0);
      setSelected(null);
      setPhase("polling");
      onActiveVector(questions[0]?.vectorIndex ?? 1);
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  }

  function pick(score: number) {
    if (!pid || selected !== null) return;
    const q = questions[qIdx];
    setSelected(score);
    send({ type: "submit_response", participantId: pid, vectorIndex: q.vectorIndex, questionId: q.id, score });
    setTimeout(() => {
      if (qIdx < questions.length - 1) {
        const n = qIdx + 1;
        setQIdx(n);
        setSelected(null);
        onActiveVector(questions[n].vectorIndex);
      } else {
        setPhase("done");
      }
    }, 350);
  }

  // After the thank-you, auto-reset for the next participant.
  useEffect(() => {
    if (phase !== "done") return;
    const t = setTimeout(() => {
      setPid(null);
      setQIdx(0);
      setSelected(null);
      setPhase("enter");
      onActiveVector(1);
    }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (phase === "enter") {
    return (
      <div className="kiosk-enter">
        <div className="kiosk-eyebrow">In-person check-in</div>
        <div className="kiosk-h">Answer at the Surface Whiteboard</div>
        <p className="kiosk-sub">
          Your responses are anonymous. Rate each factor on the maturity scale. Prefer your phone? Scan the QR or join with code <strong>{joinCode}</strong>.
        </p>
        <button className="pt-btn" onClick={start} disabled={busy} style={{ marginTop: 4 }}>
          {busy ? "Starting…" : "Start →"}
        </button>
        <MaturityStages />
      </div>
    );
  }

  if (phase === "polling") {
    const q = questions[qIdx];
    return (
      <>
        <div className="polling-progress-bar">
          <div className="polling-progress-fill" style={{ width: `${((qIdx + 1) / questions.length) * 100}%` }} />
        </div>
        <div className="polling-counter">
          Question {qIdx + 1} of {questions.length}
        </div>
        <div className="polling-question">{q.text}</div>
        <div className="polling-factor">{q.factor}</div>
        <div className="kiosk-scale-hint">Pick the maturity stage that best fits today.</div>
        <div className="pt-scale kiosk-scale">
          {MATURITY_SCALE.map((s) => (
            <button
              key={s.val}
              className={`pt-mat-opt ${selected === s.val ? "selected" : ""}`}
              style={{ ["--c" as string]: s.c } as React.CSSProperties}
              onClick={() => pick(s.val)}
              disabled={selected !== null}
            >
              <span className="pt-mat-num">{s.val}</span>
              <span className="pt-mat-icon">{s.icon}</span>
              <span className="pt-mat-text">
                <span className="pt-mat-label">{s.label}</span>
                <span className="pt-mat-blurb">{s.blurb}</span>
              </span>
            </button>
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="kiosk-done">
      <div className="kiosk-check">✓</div>
      <div className="kiosk-h">Thank you!</div>
      <p className="kiosk-sub">Your responses are in. Resetting for the next participant…</p>
    </div>
  );
}

function HeatmapView({ snap }: { snap: SessionSnapshot }) {
  const rows = snap.heatmap;
  return (
    <main className="main" style={{ gridTemplateColumns: "1fr" }}>
      <div className="panel" style={{ flex: 1, overflow: "auto", padding: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--fg-inv)", marginBottom: 12 }}>
          Participant × Vector Heatmap
        </div>
        {rows.length ? (
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12, color: "var(--fg-inv)" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "6px 12px", fontWeight: 700, borderBottom: "2px solid var(--border)" }}>Participant</th>
                {snap.vectors.map((v) => (
                  <th key={v.index} style={{ padding: "6px 10px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid var(--border)", fontSize: 10 }}>{v.shortLabel}</th>
                ))}
                <th style={{ padding: "6px 10px", textAlign: "center", fontWeight: 700, borderBottom: "2px solid var(--border)" }}>Avg</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.participantId}>
                  <td style={{ padding: "8px 12px", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>{row.name}</td>
                  {row.scores.map((s, i) => (
                    <td key={i} style={{ padding: "8px 10px", textAlign: "center", background: s === null ? "transparent" : heatColor(s), borderBottom: "1px solid var(--border)", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                      {s === null ? "—" : s.toFixed(1)}
                    </td>
                  ))}
                  <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, borderBottom: "1px solid var(--border)" }}>{row.average === null ? "—" : row.average.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ color: "var(--muted)", fontSize: 13, padding: 20 }}>No per-participant data yet. Once participants respond or you ingest a CSV, the heatmap fills in.</div>
        )}
        <div style={{ marginTop: 16, display: "flex", gap: 12, fontSize: 11, color: "var(--muted)", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700 }}>Legend:</span>
          <span style={{ background: "#dcfce7", padding: "3px 10px", borderRadius: 4, color: "#166534" }}>4–5 Transforming/Leading</span>
          <span style={{ background: "#fef9c3", padding: "3px 10px", borderRadius: 4, color: "#854d0e" }}>3–4 Optimizing</span>
          <span style={{ background: "#fed7aa", padding: "3px 10px", borderRadius: 4, color: "#9a3412" }}>2–3 Experimenting</span>
          <span style={{ background: "#fee2e2", padding: "3px 10px", borderRadius: 4, color: "#991b1b" }}>1–2 Ideating</span>
        </div>
      </div>
    </main>
  );
}
